<template>
  <div class="checkin-card">
    <attendanceCheckinModal v-if="showCheckinModal" :employeeId="employeeId"
      @close-attendance-checkin-modal="showCheckinModal = false" @refresh-attendance="onAttendanceLogged()" />
    <attendanceCheckoutModal v-if="showCheckoutModal" :employeeId="employeeId"
      @close-attendance-checkout-modal="showCheckoutModal = false" @refresh-attendance="onAttendanceLogged()" />

    <div class="checkin-top">
      <div class="checkin-top-main">
        <div class="day-label">{{ todayName }} · Attendance</div>
        <div v-if="hasScheduleToday" class="shift-label">
          <i class="pi pi-clock"></i>
          {{ formatScheduleTime(todaysSchedule.clockInTime) }} – {{ formatScheduleTime(todaysSchedule.clockOutTime) }}
        </div>
        <div v-else-if="employeeAttendanceShift" class="shift-label">
          <i class="pi pi-clock"></i>No attendance scheduled today
        </div>
        <div v-else-if="!isLoading" class="shift-label">
          <i class="pi pi-info-circle"></i>Attendance is not assigned to you yet
        </div>
      </div>
      <div class="checkin-clock">{{ clockTime }}<small>{{ clockPeriod }}</small></div>
    </div>

    <div class="checkin-body">
      <div v-if="isLoading">
        <div class="dash-skeleton" style="height: 36px; width: 70%; margin-bottom: 14px;"></div>
        <div class="dash-skeleton" style="height: 42px; width: 100%;"></div>
      </div>

      <template v-else>
        <div v-if="hasScheduleToday && !holiday" class="checkin-timeline">
          <div :class="['checkin-step', isCheckedIn ? 'done' : canCheckIn ? 'active' : '']">
            <span class="step-icon"><i :class="['pi', isCheckedIn ? 'pi-check' : 'pi-sign-in']"></i></span>
            <div class="step-text">
              <p class="step-title">Check-in</p>
              <p :class="['step-value', isCheckedIn ? '' : 'muted']">
                {{ isCheckedIn ? $filters.getTimeOnly(checkInLog.logTime) : 'Not yet' }}
              </p>
            </div>
          </div>
          <div :class="['checkin-connector', isCheckedIn ? 'done' : '']"></div>
          <div :class="['checkin-step', isCheckedOut ? 'done' : canCheckOut ? 'active' : '']">
            <span class="step-icon"><i :class="['pi', isCheckedOut ? 'pi-check' : 'pi-sign-out']"></i></span>
            <div class="step-text">
              <p class="step-title">Check-out</p>
              <p :class="['step-value', isCheckedOut ? '' : 'muted']">
                {{ isCheckedOut ? $filters.getTimeOnly(checkOutLog.logTime) : 'Not yet' }}
              </p>
            </div>
          </div>
          <div :class="['checkin-connector', isCheckedOut ? 'done' : '']"></div>
          <div class="checkin-step checkin-step--compact">
            <span :class="['step-icon', isCheckedIn ? 'active-hours' : '']"><i class="pi pi-hourglass"></i></span>
            <div class="step-text">
              <p class="step-title">Hours today</p>
              <p :class="['step-value', isCheckedIn ? '' : 'muted']">{{ hoursWorkedLabel }}</p>
            </div>
          </div>
        </div>

        <!-- Contextual messages -->
        <div v-if="holiday" class="checkin-message info">
          <i class="pi pi-star"></i>
          <span>Today is a holiday<template v-if="holiday.name"> — <strong>{{ holiday.name }}</strong></template>. No attendance is required. Enjoy your day!</span>
        </div>
        <div v-else-if="!employeeAttendanceShift" class="checkin-message warn">
          <i class="pi pi-exclamation-triangle"></i>
          <span>You have not been assigned an attendance schedule yet. Please contact your HR administrator.</span>
        </div>
        <div v-else-if="!hasScheduleToday" class="checkin-message">
          <i class="pi pi-calendar"></i>
          <span>No attendance scheduled today.<template v-if="nextSchedule"> Your next working day is <strong>{{ nextSchedule.dayOfTheWeek }}</strong> at {{ formatScheduleTime(nextSchedule.clockInTime) }}.</template></span>
        </div>
        <template v-else>
          <div v-if="isCheckedOut" class="checkin-message">
            <i class="pi pi-check-circle"></i>
            <span>You're all done for today. <template v-if="nextSchedule"> See you <strong>{{ nextSchedule.dayOfTheWeek }}</strong>.</template></span>
          </div>
          <div v-else-if="isCheckedIn && !withinShift" class="checkin-message warn">
            <i class="pi pi-exclamation-circle"></i>
            <span>The check-out window for today has closed.<template v-if="nextSchedule"> See you <strong>{{ nextSchedule.dayOfTheWeek }}</strong>.</template></span>
          </div>
          <div v-else-if="isCheckedIn && isLate" class="checkin-message warn">
            <i class="pi pi-clock"></i>
            <span>You checked in <strong>{{ lateByLabel }}</strong> after your scheduled start. Remember to check out when you finish.</span>
          </div>
          <div v-else-if="isCheckedIn" class="checkin-message">
            <i class="pi pi-check-circle"></i>
            <span>You're checked in and on time. Remember to check out when you finish.</span>
          </div>
          <div v-else-if="beforeShift" class="checkin-message info">
            <i class="pi pi-clock"></i>
            <span>Check-in opens at <strong>{{ formatScheduleTime(todaysSchedule.earlyCheckinTime ?? todaysSchedule.clockInTime) }}</strong>. Starts in {{ startsInLabel }}.</span>
          </div>
          <div v-else-if="!withinShift" class="checkin-message warn">
            <i class="pi pi-exclamation-circle"></i>
            <span>You did not log attendance today.<template v-if="nextSchedule"> See you <strong>{{ nextSchedule.dayOfTheWeek }}</strong>.</template></span>
          </div>
          <div v-else class="checkin-message">
            <i class="pi pi-map-marker"></i>
            <span>You're within the attendance time. Check in to start your day.</span>
          </div>

          <div class="checkin-actions">
            <button v-if="canCheckIn" class="btn btn-checkin" @click="showCheckinModal = true">
              <i class="pi pi-sign-in"></i> Check In
            </button>
            <button v-if="canCheckOut" class="btn btn-checkin btn-checkout" @click="showCheckoutModal = true">
              <i class="pi pi-sign-out"></i> Check Out
            </button>
            <div class="checkin-stats">
              <span v-if="checkInLog && checkInLog.locationName"><i class="pi pi-map-marker" style="font-size: 0.7rem;"></i> {{ checkInLog.locationName }}</span>
              <span v-if="isCheckedIn && !isCheckedOut">Started <strong>{{ $filters.getTimeOnly(checkInLog.logTime) }}</strong></span>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import attendanceCheckinModal from '@/components/attendance/Attendance_Checkin_Modal.vue';
import attendanceCheckoutModal from '@/components/attendance/Attendance_Checkout_Modal.vue';
import { CHECK_HOLIDAY, GET_ATTENDANCE_LOGS, GET_EMPLOYEE_ATTENDANCE_SHIFT } from '@/store/action_types';
import { DAYS_OF_THE_WEEK } from '@/utils/const';
import { AttendanceLogType } from '@/utils/enums';

export default {
  name: 'AttendanceCheckinCard',
  emits: ['attendance-changed'],
  components: {
    attendanceCheckinModal,
    attendanceCheckoutModal,
  },
  props: {
    employeeId: { type: String, required: true },
  },
  data() {
    return {
      now: new Date(),
      timer: null,
      showCheckinModal: false,
      showCheckoutModal: false,
      checkInLog: null,
      checkOutLog: null,
      todaysSchedule: null,
      nextSchedule: null,
      holidayChecked: false,
      logsLoaded: false,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    attendanceLogs() {
      return this.$store.getters.attendanceLogs.data;
    },
    employeeAttendanceShift() {
      return this.$store.getters.employeeAttendanceShift.data;
    },
    employeeAttendanceShiftIsLoading() {
      return this.$store.getters.employeeAttendanceShift.isLoading;
    },
    holiday() {
      return this.holidayChecked ? this.$store.getters.holiday.data : null;
    },
    isLoading() {
      return this.employeeAttendanceShiftIsLoading || (!this.logsLoaded && !!this.employeeAttendanceShift);
    },
    todayName() {
      const jsDay = this.now.getDay();
      return DAYS_OF_THE_WEEK[(jsDay + 6) % 7];
    },
    hasScheduleToday() {
      return !!(this.todaysSchedule && this.todaysSchedule.clockInTime && this.todaysSchedule.clockOutTime);
    },
    isCheckedIn() {
      return !!this.checkInLog;
    },
    isCheckedOut() {
      return !!this.checkOutLog;
    },
    withinShift() {
      return this.hasScheduleToday ? this.isNowWithinShift(this.todaysSchedule) : false;
    },
    beforeShift() {
      return this.hasScheduleToday ? this.isNowBeforeShift(this.todaysSchedule) : false;
    },
    canCheckIn() {
      return this.hasScheduleToday && !this.holiday && !this.isCheckedIn && this.withinShift;
    },
    canCheckOut() {
      return this.hasScheduleToday && !this.holiday && this.isCheckedIn && !this.isCheckedOut && this.withinShift;
    },
    clockTime() {
      let hours = this.now.getHours();
      const minutes = String(this.now.getMinutes()).padStart(2, '0');
      const seconds = String(this.now.getSeconds()).padStart(2, '0');
      hours = hours % 12 || 12;
      return `${String(hours).padStart(2, '0')}:${minutes}:${seconds}`;
    },
    clockPeriod() {
      return this.now.getHours() >= 12 ? 'PM' : 'AM';
    },
    workedMinutes() {
      if (!this.checkInLog) return null;
      const start = new Date(this.checkInLog.logTime);
      const end = this.checkOutLog ? new Date(this.checkOutLog.logTime) : this.now;
      return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60000));
    },
    hoursWorkedLabel() {
      if (this.workedMinutes === null) return '—';
      const h = Math.floor(this.workedMinutes / 60);
      const m = this.workedMinutes % 60;
      return `${h}h ${String(m).padStart(2, '0')}m`;
    },
    isLate() {
      if (!this.checkInLog || !this.hasScheduleToday) return false;
      const scheduled = this.$filters.dateToLocalDateTime(this.todaysSchedule.clockInTime);
      return new Date(this.checkInLog.logTime) > scheduled;
    },
    lateByLabel() {
      if (!this.isLate) return '';
      const scheduled = this.$filters.dateToLocalDateTime(this.todaysSchedule.clockInTime);
      const mins = Math.round((new Date(this.checkInLog.logTime).getTime() - scheduled.getTime()) / 60000);
      return this.minutesLabel(mins);
    },
    startsInLabel() {
      if (!this.hasScheduleToday) return '';
      const opensAt = this.$filters.dateToLocalDateTime(this.todaysSchedule.earlyCheckinTime ?? this.todaysSchedule.clockInTime);
      const mins = Math.max(0, Math.round((opensAt.getTime() - this.now.getTime()) / 60000));
      return this.minutesLabel(mins);
    },
  },
  methods: {
    minutesLabel(mins) {
      if (mins < 60) return `${mins} min`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },
    formatScheduleTime(time) {
      if (!time) return '';
      return this.$filters.formatTimeToHHMM(this.$filters.toLocalHHMMSS(time));
    },
    isNowWithinShift(schedule) {
      if (!schedule.clockInTime || !schedule.clockOutTime) return false;
      const now = this.now;
      let clockIn;
      let clockOut;
      if (schedule.earlyCheckinTime && schedule.lateCheckoutTime) {
        clockIn = this.$filters.dateToLocalDateTime(schedule.earlyCheckinTime);
        clockOut = this.$filters.dateToLocalDateTime(schedule.lateCheckoutTime);
      } else {
        clockIn = this.$filters.dateToLocalDateTime(schedule.clockInTime);
        clockOut = this.$filters.dateToLocalDateTime(schedule.clockOutTime);
      }
      return now >= clockIn && now <= clockOut;
    },
    isNowBeforeShift(schedule) {
      if (!schedule.clockInTime || !schedule.clockOutTime) return false;
      const clockIn = schedule.earlyCheckinTime && schedule.lateCheckoutTime
        ? this.$filters.dateToLocalDateTime(schedule.earlyCheckinTime)
        : this.$filters.dateToLocalDateTime(schedule.clockInTime);
      return this.now < clockIn;
    },
    resolveSchedules() {
      const schedules = this.employeeAttendanceShift?.attendanceShift?.attendanceSchedules || [];
      const todayName = this.todayName;
      this.todaysSchedule = schedules.find((e) => e.dayOfTheWeek === todayName) ?? null;

      let next = null;
      const todayIndex = DAYS_OF_THE_WEEK.findIndex((d) => d.toLowerCase() === todayName.toLowerCase());
      for (let i = 1; i <= DAYS_OF_THE_WEEK.length; i++) {
        const idx = (todayIndex + i) % DAYS_OF_THE_WEEK.length;
        const candidate = schedules.find((e) => e.dayOfTheWeek === DAYS_OF_THE_WEEK[idx]);
        if (candidate && candidate.clockInTime && candidate.clockOutTime) {
          next = candidate;
          break;
        }
      }
      this.nextSchedule = next;
    },
    getEmployeeAttendanceLogs() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const startDate = `${year}-${month}-${day}T00:00:00`;
      const endDate = `${year}-${month}-${day}T23:59:59`;

      return this.$store
        .dispatch(GET_ATTENDANCE_LOGS, {
          startDate,
          endDate,
          employeeId: this.employeeId,
          organizationId: this.account.organizationId,
        })
        .then((res) => {
          this.logsLoaded = true;
          if (res) {
            const logs = this.attendanceLogs || [];
            this.checkInLog = logs.find((e) => e.type === AttendanceLogType.checkIn) ?? null;
            this.checkOutLog = logs.find((e) => e.type === AttendanceLogType.checkOut) ?? null;
          } else {
            this.checkInLog = null;
            this.checkOutLog = null;
          }
        });
    },
    getEmployeeAttendanceShift() {
      this.$store
        .dispatch(GET_EMPLOYEE_ATTENDANCE_SHIFT, { employeeId: this.employeeId, organizationId: this.account.organizationId })
        .then((res) => {
          if (res) {
            this.resolveSchedules();
            this.getEmployeeAttendanceLogs();
          } else {
            this.logsLoaded = true;
          }
        });
    },
    checkHoliday() {
      this.$store
        .dispatch(CHECK_HOLIDAY, { date: new Date(), organizationId: this.account.organizationId })
        .finally(() => {
          this.holidayChecked = true;
        });
    },
    onAttendanceLogged() {
      this.getEmployeeAttendanceLogs().then(() => {
        this.$emit('attendance-changed');
      });
    },
    refresh() {
      this.getEmployeeAttendanceShift();
    },
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = new Date();
    }, 1000);
    this.checkHoliday();
    this.getEmployeeAttendanceShift();
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>

<style scoped>
.checkin-step .step-icon.active-hours {
  border-color: #2E7918;
  color: #2E7918;
}
</style>
